import React, { useEffect, useState, useMemo } from "react";
import {
  IconDownload,
  IconRotateClockwise,
  IconSearch,
  IconChevronLeft,
  IconChevronRight,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import {
  useFetchOrdersData,
  useOrderSyncMutation,
} from "../../../apis/orderApis";
import { useIntegrationChannelsAll } from "../../../apis/integrationApis";
import NewSelectInput from "../../../components/NewSelectInput";
// Reuse the same table as CreatedOrders for consistent design
import AllOrderTable from "../../CreatedOrders/_components/AllOrderTable";
import useDebounce from "../../../utilities/useDebounce";
import EntriesSelector from "../../../components/common/EntriesSelector";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import { T, useLanguage } from "../../../context/LanguageContext";
import { usePermission } from "../../../hooks/usePermissionTabs";

const PAGE_LIMIT = 10;

type Props = {
  setHeaderActions?: (node: React.ReactNode) => void;
  setHeaderButtons?: (node: React.ReactNode) => void;
};

export default function PendingOrders({
  setHeaderActions,
  setHeaderButtons,
}: Props = {}) {
  const { allowed: canUpdateOrder } = usePermission("orders", "update");
  const { t } = useLanguage();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [selectedMerchant, setSelectedMerchant] = useState<string>("All");
  const [selectedPlatform, setSelectedPlatform] = useState<string>("All");
  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Columns visibility
  const [columns, setColumns] = useState([
    { id: 1, text: "S/N", value: "sn", isShow: true },
    { id: 2, text: "TAG", value: "tag", isShow: true },
    { id: 3, text: "DATE", value: "createdDate", isShow: true },
    { id: 4, text: "PLATFORM", value: "platform", isShow: true },
    { id: 5, text: "AWB & REF", value: "awbRef", isShow: true },
    { id: 6, text: "WAREHOUSE", value: "warehouse", isShow: true },
    { id: 7, text: "ROUTE", value: "route", isShow: true },
    {
      id: 8,
      text: "3PL SHIPPING DETAILS",
      value: "shippingDetails",
      isShow: true,
    },
    { id: 9, text: "ORIGIN", value: "origin", isShow: true },
    { id: 10, text: "DESTINATION", value: "destination", isShow: true },
    { id: 11, text: "RECEIVER", value: "receiverDetails", isShow: true },
    { id: 12, text: "PAYMENT", value: "paymentInfo", isShow: true },
    { id: 13, text: "MERCHANT", value: "merchant", isShow: true },
    { id: 14, text: "STATUS", value: "status", isShow: true },
    { id: 15, text: "ACTIONS", value: "actions", isShow: true },
  ]);

  const handleSetHeadings = (value: string | undefined, checked: boolean) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const debouncedSearch = useDebounce(searchFor, 500);

  const { mutate: syncMutate } = useOrderSyncMutation();

  const handleSync = () => {
    setIsSyncing(true);
    syncMutate(undefined, {
      onSuccess: () => {
        refetch();
        toast.success("Orders synchronized successfully");
        setIsSyncing(false);
      },
      onError: (error: any) => {
        toast.error(error?.message || "Failed to sync orders");
        setIsSyncing(false);
      },
    });
  };

  // Fetch pending orders — same API as old AllOrders with pending=true
  const {
    data: ordersData,
    isPending: isLoading,
    refetch,
  } = useFetchOrdersData(
    currentPage,
    limit,
    debouncedSearch,
    false, // isReverse
    true, // pending
    false, // isCancelled
    false, // isDeleted
    [], // filters
  );

  const orders = ordersData?.orders ?? [];
  const totalPages = ordersData?.totalPages ?? 1;
  const totalOrders = ordersData?.total ?? 0;

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedOrders([]);
  }, [debouncedSearch, selectedMerchant, selectedPlatform]);

  useEffect(() => {
    refetch();
  }, [currentPage, debouncedSearch, refetch]);

  useEffect(() => {
    if (setHeaderActions) setHeaderActions(null);
  }, [setHeaderActions]);

  useEffect(() => {
    if (!setHeaderButtons) return;

    setHeaderButtons(
      <div className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-1.5 rounded-xl border border-slate-200 shadow-sm text-[11px] select-none font-bold min-h-8">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">{t("Selected:")}</span>
          <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
            {selectedOrders.length}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-300" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">{t("Total:")}</span>
          <span className="text-slate-700 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded">
            {totalOrders}
          </span>
        </div>
      </div>,
    );

    return () => setHeaderButtons(null);
  }, [setHeaderButtons, selectedOrders.length, totalOrders, t]);

  const { data: shippingPlatforms } = useIntegrationChannelsAll("shipping");

  const uniqueMerchants = useMemo(
    () =>
      Array.from(new Set(orders.map((o: any) => o.clientName).filter(Boolean))),
    [orders],
  );

  const uniquePlatforms = useMemo(() => {
    const set = new Set<string>();
    set.add("Salla");
    set.add("Zid");
    if (shippingPlatforms) {
      shippingPlatforms.forEach((sp: any) => {
        if (sp.companyName) set.add(sp.companyName);
      });
    }
    return Array.from(set);
  }, [shippingPlatforms]);

  const filteredOrders = useMemo(() => {
    return orders.filter((order: any) => {
      const matchesMerchant =
        selectedMerchant === "All" || order.clientName === selectedMerchant;
      const matchesPlatform =
        selectedPlatform === "All" ||
        order.platform === selectedPlatform ||
        order.shippingCompany === selectedPlatform;
      return matchesMerchant && matchesPlatform;
    });
  }, [orders, selectedMerchant, selectedPlatform]);

  const handleSelectOrder = (order: any) => {
    setSelectedOrders((prev) => {
      if (prev.some((o) => o.id === order.id))
        return prev.filter((o) => o.id !== order.id);
      return [...prev, order];
    });
  };

  const handleReset = () => {
    setSearchFor("");
    setSelectedMerchant("All");
    setSelectedPlatform("All");
    setSelectedOrders([]);
    setCurrentPage(1);
    toast.success("Filters reset successfully");
  };

  const handleExportXLSX = () => {
    const dataToExport =
      selectedOrders.length > 0 ? selectedOrders : filteredOrders;
    if (dataToExport.length === 0) {
      toast.error("No data available to export");
      return;
    }
    const data = dataToExport.map((order: any, idx: number) => ({
      "S/N": idx + 1,
      Tag: order.orderType,
      "Order Date": new Date(order.createdAt).toLocaleDateString(),
      Platform: order.platform,
      "AWB Number": order.awbNumber,
      Reference: order.referenceNumber,
      Warehouse: order.warehouseName,
      Origin: order.senderCityName,
      "Destination Country": order.receiverCountryName,
      "Destination City": order.receiverCityName,
      "Destination District": order.receiverDistrictName,
      "Receiver Name": order.receiverName,
      "Receiver Mobile": order.receiverMobile,
      "Payment Mode": order.paymentType,
      "Payment Amount": order.codAmount,
      Merchant: order.clientName,
      Status: order.status,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pending Orders");
    XLSX.writeFile(wb, "pending_orders_export.xlsx");
    toast.success("Exported successfully!");
  };

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setSelectedOrders([]);
  };

  const renderPageNumbers = () => {
    const pages: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="max-w-full">
      {/* ── Main Card (Header + Table Joined) ─────────────────────── */}
      <div className=" p-2  bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-ful rounded-r-3xl rounded-bl-3xl w-full">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <div className="px-6 py-1.5">
          {/* Header Row (Filters + Actions Unified) */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Left side: Badge + Filters Shifted */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Merchant filter */}
            </div>

            {/* Right side: Export + Search + Sync + Selected Badge */}
            <div className="flex items-center flex-wrap gap-2.5">
              <NewSelectInput
                name="merchant-filter"
                placeholder={t("Merchant")}
                value={selectedMerchant === "All" ? "" : selectedMerchant}
                setValue={(val) => setSelectedMerchant(val || "All")}
                selectOptions={uniqueMerchants.map((m: any) => ({
                  id: m,
                  value: m,
                  text: m,
                }))}
                className="w-fit"
                removeDiv
                compact
              />

              {/* Platform filter */}
              <NewSelectInput
                name="platform-filter"
                placeholder={t("Platform")}
                value={selectedPlatform === "All" ? "" : selectedPlatform}
                setValue={(val) => setSelectedPlatform(val || "All")}
                selectOptions={uniquePlatforms.map((p: any) => ({
                  id: p,
                  value: p,
                  text: p,
                }))}
                className="w-fit"
                removeDiv
                compact
              />

              {/* Reset Button */}
              <button
                onClick={handleReset}
                className="flex items-center justify-center p-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-slate-300 text-slate-700 rounded-md transition shadow-sm active:scale-95 duration-150"
                title={t("Reset Filters")}
              >
                <IconRotateClockwise
                  size={17}
                  stroke={2.5}
                  className="text-slate-500 animate-hover-spin"
                />
              </button>
              {/* Sliding Search */}
              {/* Sliding Search */}
              <div className="relative flex items-center group">
                <div
                  onClick={() => {
                    if (!showSearch) {
                      setShowSearch(true);
                    }
                  }}
                  className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                    showSearch
                      ? "w-64 px-2 cursor-default"
                      : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                  }`}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSearch(!showSearch);
                    }}
                    className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                  >
                    <IconSearch size={17} stroke={2.5} />
                  </button>
                  {showSearch && (
                    <>
                      <input
                        type="text"
                        value={searchFor}
                        onChange={(e) => setSearchFor(e.target.value)}
                        placeholder={t("Search...")}
                        autoFocus
                        className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchFor("");
                          setShowSearch(false);
                        }}
                        className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                        title={t("Clear & Close")}
                      >
                        <IconX size={14} stroke={2.5} />
                      </button>
                    </>
                  )}
                </div>
                {!showSearch && (
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                    {t("Search")}
                  </div>
                )}
              </div>

              {/* Export */}
              <div className="relative group">
                <button
                  onClick={handleExportXLSX}
                  className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                >
                  <IconDownload size={17} stroke={2.5} />
                </button>
                <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                  {t("Export to Excel")}
                </div>
              </div>

              {/* Sync Button */}
              {canUpdateOrder && (
                <div className="relative group">
                  <button
                    onClick={handleSync}
                    disabled={isSyncing}
                    className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <IconRefresh
                      size={17}
                      stroke={2.5}
                      className={isSyncing ? "animate-spin" : ""}
                    />
                  </button>
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                    {t("Sync pending orders")}
                  </div>
                </div>
              )}

              {/* Entries Limit Selector */}
              {ordersData?.totalPages && ordersData.totalPages * limit > 10 && (
                <EntriesSelector
                  value={limit}
                  options={[
                    10,
                    ...(ordersData.totalPages * limit > 10 ? [20] : []),
                    ...(ordersData.totalPages * limit > 20 ? [30] : []),
                    ...(ordersData.totalPages * limit > 50 ? [50] : []),
                    ...(ordersData.totalPages * limit > 50 ? [100] : []),
                  ]}
                  onChange={(val) => {
                    setLimit(val);
                    setCurrentPage(1);
                  }}
                />
              )}

              {/* Columns Hide/Show Filter */}
              <div className="relative group">
                <ColumnsFilter
                  columnsData={columns}
                  setHeadings={handleSetHeadings}
                />
                <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                  {t("Columns View")}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table Container (Joined) */}
        <div className="border-t border-slate-100">
          {isLoading ? (
            <div className="py-16 text-center bg-white">
              <p className="text-slate-400 text-sm font-semibold animate-pulse">
                {t("Loading orders...")}
              </p>
            </div>
          ) : filteredOrders.length > 0 ? (
            <AllOrderTable
              data={{ orders: filteredOrders }}
              columnsData={columns}
              currentPage={currentPage}
              limitPage={limit}
              isCheckboxEnabled={true}
              selectedOrders={selectedOrders}
              onSelectOrder={handleSelectOrder}
            />
          ) : (
            <div className="py-16 text-center bg-white">
              <p className="text-slate-400 text-sm font-semibold">
                {t("No pending orders found")}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-2 pb-4">
          <p className="text-xs text-slate-500 font-semibold">
            {t("Page")}{" "}
            <span className="text-slate-800 font-black">{currentPage}</span> {t("of")}{" "}
            <span className="text-slate-800 font-black">{totalPages}</span>
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
            >
              <IconChevronLeft size={14} stroke={2.5} />
            </button>

            {renderPageNumbers().map((p, i) =>
              p === "..." ? (
                <span
                  key={`ellipsis-${i}`}
                  className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => goToPage(p as number)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-bold transition shadow-sm ${
                    currentPage === p
                      ? "bg-[#FBD037] border-[#FBD037] text-[#131327]"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {p}
                </button>
              ),
            )}

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
            >
              <IconChevronRight size={14} stroke={2.5} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
